import { useState, useEffect, useCallback, useMemo } from 'react'
import type {
  MapStyle,
  CanvasStateOutput,
  UpdateCanvasStateInput,
  BulkCanvasStateItem,
} from '../../types/infrastructure-map'
import { logger } from '../../utils/logger'
import {
  listCanvasStates,
  updateCanvasState,
  bulkUpdateCanvasStates,
} from '../../services/infrastructure-map'

export interface UseCanvasStatesResult {
  canvasStates: CanvasStateOutput[]
  canvasStatesMap: Map<string, CanvasStateOutput>
  loading: boolean
  error: string | null
  updateState: (
    endpointId: string,
    input: UpdateCanvasStateInput
  ) => Promise<CanvasStateOutput | null>
  bulkUpdate: (states: BulkCanvasStateItem[]) => Promise<number>
  refetch: () => Promise<void>
}

function buildCanvasStatesMap(states: CanvasStateOutput[]): Map<string, CanvasStateOutput> {
  const map = new Map<string, CanvasStateOutput>()
  for (const state of states) {
    map.set(state.endpointId, state)
  }
  return map
}

function mergeUpdated(
  prev: CanvasStateOutput[],
  endpointId: string,
  updated: CanvasStateOutput
): CanvasStateOutput[] {
  return prev.some((state) => state.endpointId === endpointId)
    ? prev.map((state) => (state.endpointId === endpointId ? updated : state))
    : [...prev, updated]
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

interface FetchOptions {
  caseId: string | null
  mapStyle: MapStyle
  setCanvasStates: (states: CanvasStateOutput[]) => void
  setLoading: (next: boolean) => void
  setError: (next: string | null) => void
}

function useFetchCanvasStates(options: FetchOptions): () => Promise<void> {
  const { caseId, mapStyle, setCanvasStates, setLoading, setError } = options
  return useCallback(async () => {
    if (!caseId) {
      setCanvasStates([])
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await listCanvasStates(caseId, mapStyle)
      setCanvasStates(data.canvasStates)
    } catch (err) {
      logger.warn('useCanvasStates', 'Failed to fetch canvas states', err)
      setError(errorMessage(err, 'Failed to fetch canvas states'))
    } finally {
      setLoading(false)
    }
  }, [caseId, mapStyle, setCanvasStates, setLoading, setError])
}

interface UpdateStateOptions {
  caseId: string | null
  mapStyle: MapStyle
  setCanvasStates: React.Dispatch<React.SetStateAction<CanvasStateOutput[]>>
  setError: (next: string | null) => void
}

function useUpdateState(
  options: UpdateStateOptions
): (id: string, input: UpdateCanvasStateInput) => Promise<CanvasStateOutput | null> {
  const { caseId, mapStyle, setCanvasStates, setError } = options
  return useCallback(
    async (endpointId, input) => {
      if (!caseId) return null
      try {
        const updated = await updateCanvasState({ caseId, endpointId, mapStyle, input })
        setCanvasStates((prev) => mergeUpdated(prev, endpointId, updated))
        setError(null)
        return updated
      } catch (err) {
        logger.warn('useCanvasStates', 'Failed to update canvas state', err)
        setError(errorMessage(err, 'Failed to update canvas state'))
        return null
      }
    },
    [caseId, mapStyle, setCanvasStates, setError]
  )
}

interface BulkUpdateOptions {
  caseId: string | null
  mapStyle: MapStyle
  refetch: () => Promise<void>
  setError: (next: string | null) => void
}

function useBulkUpdate(
  options: BulkUpdateOptions
): (states: BulkCanvasStateItem[]) => Promise<number> {
  const { caseId, mapStyle, refetch, setError } = options
  return useCallback(
    async (states) => {
      if (!caseId || states.length === 0) return 0
      try {
        const result = await bulkUpdateCanvasStates(caseId, mapStyle, { states })
        await refetch()
        setError(null)
        return result.updated
      } catch (err) {
        logger.warn('useCanvasStates', 'Failed to bulk update canvas states', err)
        setError(errorMessage(err, 'Failed to bulk update canvas states'))
        return 0
      }
    },
    [caseId, mapStyle, refetch, setError]
  )
}

export function useCanvasStates(caseId: string | null, mapStyle: MapStyle): UseCanvasStatesResult {
  const [canvasStates, setCanvasStates] = useState<CanvasStateOutput[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canvasStatesMap = useMemo(() => buildCanvasStatesMap(canvasStates), [canvasStates])
  const refetch = useFetchCanvasStates({
    caseId,
    mapStyle,
    setCanvasStates,
    setLoading,
    setError,
  })
  const updateState = useUpdateState({ caseId, mapStyle, setCanvasStates, setError })
  const bulkUpdate = useBulkUpdate({ caseId, mapStyle, refetch, setError })

  useEffect(() => {
    void refetch()
  }, [refetch])

  return { canvasStates, canvasStatesMap, loading, error, updateState, bulkUpdate, refetch }
}
